import React, { useEffect, useState } from "react"
import without from "underscore/modules/without"
import findWhere from "underscore/modules/findWhere"

import Spinner from "../../../../../../ui-components/Spinner"
import StripePaymentMethodsList from "./StripePaymentMethodsList"
import AddStripeCreditCardModal from "./AddStripeCreditCardModal"
import AlertModal from "../../../../../../ui-components/AlertModal"
import { intercomTrackEvent } from "../../../../../../utils/Intercom/intercomHelper"
import { CHANGE_PAYMENT_METHOD } from "../../../../../../stores/CartStateStore"
import CreditCardsAPI from "../../../../../../apis/CreditCardsAPI"

// used in AddStripeCreditCardModal
import { loadStripe } from "@stripe/stripe-js"
const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_API_KEY)

function arrayWithout(array, id) {
  return without(
    array,
    findWhere(array, {
      id: id,
    })
  )
}

export default function StripeCheckout(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    userAuthToken,
    stripe_key,
    showAlertMessage,
    venueAcceptsStripe,
    dispatchCartState,
  } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [state, setState] = useState({
    methods: [],
    loading: true,
    loaded: false,
    showNewCreditCardModal: false,
    selectedMethod: null,
    errorModalText: null,
  })

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    CreditCardsAPI.SetUserAuthToken(userAuthToken)

    loadPaymentMethods()
    intercomTrackEvent("credit-cards-tab-selected", {})
  }, [])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  async function loadPaymentMethods() {
    if (state.loaded) return

    let response = await CreditCardsAPI.GetCreditCards()

    if (response.error || response.error === "") {
      const errorMessage = !!response.error
        ? `${I18n.t("cart_summary.credit_cards.errors.cannot_get_cards")}: ${
            response.error
          }`
        : I18n.t("cart_summary.credit_cards.errors.cannot_get_cards")

      setState({
        ...state,
        errorModalText: errorMessage,
        methods: [],
        selectedMethod: null,
        loaded: true,
        loading: false,
      })
      return
    }

    let cards = response.cards || []
    let defaultMethod = response.default_source

    //Check for American Express cards
    for (let card of cards) {
      if (card.brand === "American Express") {
        deletePaymentMethod(card)
        return
      }
    }

    setState({
      ...state,
      methods: cards,
      loaded: true,
      loading: false,
      selectedMethod: defaultMethod,
    })

    if (defaultMethod) {
      // Set as current method the first card
      dispatchCartState({
        type: CHANGE_PAYMENT_METHOD,
        payload: { type: "stripe", options: { method: defaultMethod } },
      })
    } else {
      dispatchCartState({
        type: CHANGE_PAYMENT_METHOD,
        payload: { type: "stripe", options: {} },
      })
    }
  }

  function createPaymentMethod(method) {
    CreditCardsAPI.CreateCreditCard(method) // async, don't wait the result, not needed

    setState({
      ...state,
      loading: false,
      methods: [...state.methods, method],
      selectedMethod: method.id,
      showNewCreditCardModal: false,
    })
    dispatchCartState({
      type: CHANGE_PAYMENT_METHOD,
      payload: { type: "stripe", options: { method: method.id } },
    })
  }

  function changeSelectedPaymentMethod(paymentMethod) {
    setState({ ...state, selectedMethod: paymentMethod.id })
    dispatchCartState({
      type: CHANGE_PAYMENT_METHOD,
      payload: { type: "stripe", options: { method: paymentMethod.id } },
    })
  }

  function deletePaymentMethod(method) {
    CreditCardsAPI.DeleteCreditCard(method)

    setState({ ...state, methods: arrayWithout(state.methods, method.id) })
  }

  function showNewCardForm(event) {
    event.preventDefault()

    setState({ ...state, showNewCreditCardModal: true })
  }

  function hideNewCardForm() {
    setState({ ...state, showNewCreditCardModal: false })
  }

  function renderPaymentMethods(showAlertMessage) {
    if (state.methods.length <= 0) return null

    return (
      <div className="clearfix fr-margin-top-large">
        <p className="fr-p3">
          {I18n.t("cart_summary.credit_cards.pre_auth_instructions")}
        </p>
        {showAlertMessage && (
          <p className="fr-p3 fr-primary">
            <b>
              {I18n.t("cart_summary.credit_cards.delivery_price_instructions")}
            </b>
          </p>
        )}
      </div>
    )
  }

  function renderSpinner() {
    return <Spinner />
  }

  function renderNoStripe() {
    return <div>{I18n.t("cart_summary.venue_no_credit_cards")}</div>
  }

  function renderStripeForm() {
    return (
      <div>
        <AlertModal
          title="Attenzione!"
          text={state.errorModalText}
          show={!!state.errorModalText}
          onClose={() => setState({ ...state, errorModalText: null })}
        />

        <div className="row fr-margin-top-large">
          <div className="col-sm-12">
            {state.methods.length === 0 ? (
              <p>
                {I18n.t("cart_summary.credit_cards.no_credit_cards_present")}
              </p>
            ) : (
              <StripePaymentMethodsList
                methods={state.methods}
                selectedMethod={state.selectedMethod}
                onPaymentMethodSelected={changeSelectedPaymentMethod}
                onPaymentMethodDeleted={deletePaymentMethod}
              />
            )}

            <div className="item">
              <a
                className="btn btn-primary pull-right fr-margin-top-small"
                href="#"
                onClick={showNewCardForm}
              >
                {I18n.t("cart_summary.credit_cards.add_cart")}
              </a>
              <div className="clearfix" />
            </div>

            {renderPaymentMethods(showAlertMessage)}
          </div>
        </div>
        <AddStripeCreditCardModal
          stripe_key={stripe_key}
          onNewCreditCard={createPaymentMethod}
          onNewCreditCardError={(message) =>
            setState({ ...state, errorModalText: message })
          }
          onHide={hideNewCardForm}
          show={state.showNewCreditCardModal}
        />
      </div>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  // -------------------------------------

  if (state.loading) {
    return renderSpinner()
  } else {
    if (venueAcceptsStripe) {
      return renderStripeForm()
    } else {
      return renderNoStripe()
    }
  }
}
